import { useState, useEffect } from 'react';
import { Button } from '@shopify/polaris';
import { PdfCard } from '../../pages/invoice_in/components/PdfCard.jsx';
import { sortS3KeysPdfFirst } from '../util/s3FileOrdering.js';

function FullscreenModal({ open, onClose, title, children }) {
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(240, 242, 245, 0.55)',
        backdropFilter: 'blur(6px)',
        animation: 'fadeInModal 0.25s'
      }}
    >
      <style>{`
        @keyframes fadeInModal {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .pdf-modal-header {
          position: sticky;
          top: 0;
          z-index: 2;
          background: rgba(255,255,255,0.95);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 32px 0 24px;
          height: 64px;
          border-bottom: 1px solid #eee;
          font-size: 1.25rem;
          font-weight: 600;
          letter-spacing: 0.01em;
        }
        .pdf-modal-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 50%;
          transition: background 0.15s;
        }
        .pdf-modal-close:hover {
          background: #f2f2f2;
        }
        .pdf-modal-content {
          flex: 1;
          overflow: auto;
          background: #fff;
          border-radius: 0 0 18px 18px;
        }
      `}</style>
      <div
        style={{
          width: '98vw',
          maxWidth: 900,
          height: '98vh',
          maxHeight: 1100,
          background: '#fff',
          borderRadius: 18,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 320
        }}
      >
        <div className="pdf-modal-header">
          <span>{title}</span>
          <button className="pdf-modal-close" onClick={onClose} aria-label="Close PDF preview" title="Close">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="14" fill="transparent" />
              <path d="M9 9L19 19M19 9L9 19" stroke="#222" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="pdf-modal-content">{children}</div>
      </div>
    </div>
  );
}

export default function InvoicePdfPreviewButton({
  s3ObjectKeys,
  buttonLabel = 'PDF',
  buttonSize = 'slim',
  buttonEmoji = '🗄️',
  modalTitle = 'Invoice PDF Preview',
  resource = 'invoices_in',
  ...buttonProps
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [keys, setKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState();

  const handleOpen = () => {
    let parsedKeys = [];
    if (Array.isArray(s3ObjectKeys)) {
      parsedKeys = s3ObjectKeys;
    } else if (typeof s3ObjectKeys === 'string') {
      try {
        parsedKeys = JSON.parse(s3ObjectKeys);
      } catch {
        parsedKeys = [s3ObjectKeys];
      }
    }
    const orderedKeys = sortS3KeysPdfFirst(parsedKeys);
    setKeys(orderedKeys);
    setSelectedKey(orderedKeys[0]);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedKey(undefined);
  };

  return (
    <>
      <Button size={buttonSize} onClick={handleOpen} {...buttonProps} accessibilityLabel={buttonLabel}>
        {buttonEmoji}
      </Button>
      <FullscreenModal open={modalOpen} onClose={handleClose} title={modalTitle}>
        {keys.length > 1 && (
          <div style={{ padding: 12, background: '#fafbfc', borderBottom: '1px solid #eee' }}>
            <span style={{ marginRight: 8 }}>Select file:</span>
            {keys.map((key) => (
              <Button key={key} size="slim" onClick={() => setSelectedKey(key)} primary={selectedKey === key} style={{ marginRight: 4 }}>
                {key.split('/').pop()}
              </Button>
            ))}
          </div>
        )}
        <div style={{ width: '100%', height: 'calc(98vh - 64px - 48px)', minHeight: 400 }}>
          {selectedKey && <PdfCard keys={keys} selectedS3Key={selectedKey} setSelectedS3Key={setSelectedKey} resource={resource} style={{ width: '100%', height: '100%' }} />}
        </div>
      </FullscreenModal>
    </>
  );
}
